import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  SysAdminUser,
  SysAdminUserCreate,
  SysAdminUserGetFiltersResponse,
  SysAdminUserGetResponse,
  SysAdminUserUpdate,
} from '../types/sysadmin-user.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';

const useUserBasePath = () => {
  return useMemo(() => {
    return `/v1/sysadmin/users`;
  }, []);
};

export const useSysAdminUserHooks = () => {
  const userBasePath = useUserBasePath();

  return createApiHooks<
    SysAdminUser,
    SysAdminUserGetResponse,
    SysAdminUserCreate,
    SysAdminUserUpdate
  >({
    basePath: userBasePath,
    queryKey: ['SysAdminUsers'],
    apiClient,
  });
};

export const useSysAdminUserGetFilters = (queryOptions?: any) => {
  const userBasePath = useUserBasePath();
  return useGenericGet<SysAdminUserGetFiltersResponse>(
    `${userBasePath}/filters`,
    ['Users', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the user hooks
export const useSysAdminUserApi = () => {
  const sysAdminUserHooks = useSysAdminUserHooks();

  return {
    useGet: sysAdminUserHooks.useGet,
    useCreate: sysAdminUserHooks.useCreate,
    useUpdate: sysAdminUserHooks.useUpdate,
    useDelete: sysAdminUserHooks.useDelete,
    useList: (params: UseListParams) => sysAdminUserHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useSysAdminUserGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useSysAdminUserApi();
  return useGet(id, queryOptions);
};

export const useSysAdminUserCreate = (mutationOptions?: any) => {
  const { useCreate } = useSysAdminUserApi();
  return useCreate(mutationOptions);
};

export const useSysAdminUserUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useSysAdminUserApi();
  return useUpdate(mutationOptions);
};

export const useSysAdminUserDelete = (mutationOptions?: any) => {
  const { useDelete } = useSysAdminUserApi();
  return useDelete(mutationOptions);
};

export const useSysAdminUserList = (params: UseListParams) => {
  const { useList } = useSysAdminUserApi();
  return useList(params);
};
